import type {EventCalendarMetaData, EventHashedID, EventsFilters, EventsListLazyData, EventsReduxState, EventTimeFilterMap, EventVO} from '@Components/events/events.types';
import {EventSortBy, EventsSharedWithMeFilter, EventStatus, EventsViewType, EventTimeFilter} from '@Components/events/events.types';
import {appendFontStyleSheetToDOM, getFontStyleSheetPath} from '@Libraries/font-library';
import {getFontFamilyStyleString} from '@Utils/font.util';
import {
  areDatesEqual,
  convertUTCTimeToGivenTimezone,
  getDateFromUnixTimestamp,
  getReadableShortMonth,
  getUnixTimestamp,
  getYearMonthDateFormat,
  hasDatedPassed,
  isBetweenDates,
  isFutureDate,
} from '@Utils/date.util';
import {LoadingStates} from '@Utils/loading.util';
import {getUniqueString} from '@Utils/string.util';
import {openEventPreviewModal} from '@Modals/event-preview-modal/event-preview-modal.library';
import type {RecurringEvent} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import {RecurringType} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import {ALL_WEEKDAYS, Weekdays} from '@Components/event-wizard/week-day-selectors/week-day-selectors.types';
import {initSocialMediaProfileLinks} from '@Components/social-media-profile-links/social-media-profile-links-library';
import {getUserId} from '@Libraries/user.library';
import {isPAYGUser} from '@Utils/user.util';
import type {EventTypeAiSearchParams, MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {onAjaxError} from '@Utils/ajax.util';
import {noop} from '@Utils/general.util';
import {getTranslatedWeekdays} from '@Components/event-wizard/event-wizard-date-time-summary/event-wizard-date-time-summary.types';
import type {PlaceDetails} from '@Components/google-map/google-map.types';
import {cleanURIComponent} from '@Utils/url.util';
import {MY_STUFF_HASHES} from '@Libraries/my-stuff-library';
import type {TimeZone} from 'timezones-list';
import timezones from 'timezones-list';
import {getGMTOffsetMinutesFromTimeZone} from '@Components/event-wizard/timezone-content/timezone-content.helper';
import {openUpsellingDialogForEmailCampaigns} from '@Libraries/email-campaigns-library';
import {repoURL} from '@Libraries/s3-library';
import {GalleryThemeURI} from '@Components/design-gallery/design-gallery.types';
import {getWriteBucket} from '@Utils/s3.util';
import type {EventsIframeModalProps} from '@Modals/event-preview-modal/event-preview-modal.types';
import {openDeleteEventConfirmationModal} from '@Modals/delete-event-confirmation-modal/delete-event-confirmation-modal.library';
import {EventUpsellGA4Source, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {PromotionType} from '@Components/mystuff-event-promotion-page/mystuff-event-promotion-page.types';
import type {DeleteEventConfirmationPanelProps} from '@Panels/delete-event-confirmation-panel/delete-event-confirmation-panel.types';
import type {EventPendingEmailInvitation} from '@Panels/manage-event-collaborators-panel/manage-event-collaborators-panel.types';
import {getColorPaletteForContainer, PMW_COLORS_NEUTRAL} from '@Utils/color.util';

export const EVENTS_LAZY_BATCH_SIZE = 50;

export const EVENT_WIZARD_SOURCE_URL_PARAM = 'st';
export const EVENT_PAGE_SOURCE_URL_PARAM = 'st';
export const EVENT_PAGE_CONFETTI_URL_PARAM = 'sc';

export const DEFAULT_EVENTS_STYLE_FONT_FAMILY = 'Figtree';

export const EVENT_REGISTRATIONS_LIMIT_PAYG = 100;

export const EMBED_EVENT_HELP_CENTRE_ARTICLE_LINK =
  'https://support.postermywall.com/hc/en-us/articles/36928038136333-How-to-embed-your-PosterMyWall-events-schedule-on-your-website';

export const FEEDBACK_LINK = 'https://surveys.hotjar.com/fcc93b4b-cefe-4d68-889a-31900727e9dc';

export const TICKET_CHECKIN_SURVEY_HOTJAR_ID = 'event-ticketing-feedback-hotjar';
export const TICKET_CHECKIN_SURVEY_URI_SHOW = 'event/shouldShowTicketCheckinSurvey';
export const TICKET_CHECKIN_SURVEY_URI_MARK_COMPLETE = 'event/markTicketCheckinSurveyComplete';

export const EVENT_CALENDAR_EMBED_WORDPRESS_PLUGIN_URL = 'https://wordpress.org/plugins/postermywall-events-calendar/';

export interface DeleteEventFlowParams {
  eventHashedId: EventHashedID;
  onDeleteSuccess: VoidFunction;
  doShowDeleteButtonLoader?: boolean;
}

export enum EventPagePreviewType {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
}

export enum EventUpsellType {
  MORE_EVENTS,
  MORE_EVENTS_PREMIUM,
  REMOVE_WATERMARK,
  DESCRIPTION_REWRITE_AI,
  REGISTRATION_OPTIONS,
  RECURRING,
  NUM_GUESTS_LIMIT,
  VIEW_ANALYTICS,
  REMINDERS,
  SHARE_TEAM,
  SIGNUP_FORM,
  EVENT_ONE_CLICK_PROMOTION,
}

export enum EventPromoteManageGA4Source {
  MYSTUFF = 'MyStuff',
  MYSTUFF_POPUP = 'MyStuff_popup',
  PROMOTE_PAGE = 'PromotePage',
}

/**
 * query params appended to the wizard url
 * for example, for prefilling start date, we use query param 'sd'
 * ENSURE THIS IS THE SAME AS IN PHP SIDE. SEE "EventWizardURLMaker.php"
 */
export enum EventWizardPrefillValueQueryParam {
  START_DATE = 'sd',
  DESIGN_TEMPLATE_HID = 'tcph',
  DESIGN_TEMPLATE_SOURCE = 'tcps',
  DESIGN_PAGE_ID = 'dpid',
}

export enum EventWizardPrefillSource {
  DEFAULT,
  DUPLICATE,
  UPDATE,
  PREFILL_FROM_DESIGN,
  PREFILL_FROM_START_DATE_SELECTION,
}

export enum EventPageSource {
  DEFAULT,
  FROM_WIZARD_COMPLETION = 1,
  FOR_WIZARD_IFRAME = 2,
}

/**
 * starting the calendar from monday onwards for events specifically
 */
export const getTranslatedPluralWeekdaysForEvents = (): string[] => {
  return [
    window.i18next.t('pmwjs_calendar_mondays'),
    window.i18next.t('pmwjs_calendar_tuesdays'),
    window.i18next.t('pmwjs_calendar_wednesdays'),
    window.i18next.t('pmwjs_calendar_thursdays'),
    window.i18next.t('pmwjs_calendar_fridays'),
    window.i18next.t('pmwjs_calendar_saturdays'),
    window.i18next.t('pmwjs_calendar_sundays'),
  ];
};

export const isEventsFeatureEnabled = (): boolean => {
  return !!window.reactPageProps.isEventsFeatureEnabled;
};

export const isEventPromotionEnabled = (): boolean => {
  return !!window.reactPageProps.isEventPromotionEnabled;
};

export const getEventWizardURLWithPrefillSource = (prefillSource: EventWizardPrefillSource, prefillParamValue: Record<string, string> = {}, eventHashedId = ''): string => {
  const baseURI = eventHashedId ? `event/wizard/${eventHashedId}` : 'event/wizard';
  const url = new URL(window.PMW.util.site_url(baseURI));

  url.searchParams.append(EVENT_WIZARD_SOURCE_URL_PARAM, prefillSource.toString());

  Object.entries(prefillParamValue).forEach(([index, value]) => {
    url.searchParams.append(index, value);
  });
  return url.toString();
};

export const getURLToEditEvent = (event: EventVO): string => {
  return getEventWizardURLWithPrefillSource(EventWizardPrefillSource.UPDATE, {}, event.hashedID);
};

export const getURLToDuplicateEvent = (event: EventVO): string => {
  return getEventWizardURLWithPrefillSource(EventWizardPrefillSource.DUPLICATE, {}, event.hashedID);
};

export const getPromotePageURL = (event: EventVO): string => {
  return window.PMW.util.site_url(`posters/mine${MY_STUFF_HASHES.promoteEvent}/${event.hashedID}`);
};

export const startEventPromotionFlow = (event: EventVO, source: EventPromoteManageGA4Source): void => {
  if (isEventPromotionEnabled()) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_PROMOTE_CLICK, {[GA4EventParamName.SOURCE]: source.toString()});
    window.location.href = getPromotePageURL(event);
  }
};

export const startEventManageFlow = (event: EventVO, source: EventPromoteManageGA4Source): void => {
  if (isEventPromotionEnabled()) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_MANAGE_CLICK, {[GA4EventParamName.SOURCE]: source.toString()});
    window.location.href = window.PMW.util.site_url(`posters/mine${MY_STUFF_HASHES.manageEvent}/${event.hashedID}`);
  }
};

export const getEventWizardURLWithDesignPrefill = (designHID: string, designSource: MediaTypes, pageId?: string): string => {
  const params: Record<string, string> = {
    [EventWizardPrefillValueQueryParam.DESIGN_TEMPLATE_HID.toString()]: designHID,
    [EventWizardPrefillValueQueryParam.DESIGN_TEMPLATE_SOURCE.toString()]: designSource.toString(),
  };

  if (pageId) {
    params[EventWizardPrefillValueQueryParam.DESIGN_PAGE_ID.toString()] = pageId;
  }

  return getEventWizardURLWithPrefillSource(EventWizardPrefillSource.PREFILL_FROM_DESIGN, params);
};

export const getEventWizardURLWithDatePrefill = (date: Date): string => {
  return getEventWizardURLWithPrefillSource(EventWizardPrefillSource.PREFILL_FROM_START_DATE_SELECTION, {
    [EventWizardPrefillValueQueryParam.START_DATE.toString()]: getYearMonthDateFormat(date),
  });
};

export const openEventWizardWithStartDatePrefillFromContentPlanner = (date: Date): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_CLICK, {[GA4EventParamName.SOURCE]: 'Content planner'});
  openEventWizardWithStartDatePrefill(date);
};

export const openEventWizardWithStartDatePrefill = (date: Date): void => {
  window.location.href = getEventWizardURLWithDatePrefill(date);
};

export const getEventPageURL = (event: EventVO): string => {
  return window.PMW.util.site_url(getBaseEventPageURL(event));
};

export const getEventsCalendarPageURL = (hashedId: string): string => {
  return window.PMW.util.site_url(`e/calendar/${hashedId}`);
};

export const getEventPageURLWithSource = (source: EventPageSource, event: EventVO | undefined = undefined, isUserFirstEvent = false): string => {
  const baseURI = event ? getBaseEventPageURL(event) : 'e';
  const url = new URL(window.PMW.util.site_url(baseURI));

  if (areQueryParamsNotNeededForEventPageSource(source)) {
    return url.toString();
  }
  url.searchParams.append(EVENT_PAGE_SOURCE_URL_PARAM, source.toString());
  url.searchParams.append(EVENT_PAGE_CONFETTI_URL_PARAM, isUserFirstEvent ? '1' : '0');
  return url.toString();
};

export const getBaseEventPageURL = (event: EventVO): string => {
  const baseURL = 'e';

  const venue = getVenueSEOForEventPageURL(event.venue);
  const eventTypeName = event.type.name;

  const seoURI = cleanURIComponent(`${venue ? venue : ''}${eventTypeName ? `-${eventTypeName}` : ''}-${event.title}`);
  const siteStr = `${baseURL}/${seoURI}/${event.hashedID}`;

  return siteStr.toLowerCase();
};

const getVenueSEOForEventPageURL = (placeDetails: PlaceDetails | undefined): string => {
  let venue = 'online';

  if (placeDetails) {
    const city = placeDetails.city;
    const country = placeDetails.country;

    if (city && city.trim() !== '' && country && country.trim() !== '') {
      venue = `${city}-${country}`;
    } else if (city) {
      venue = city;
    } else if (country) {
      venue = country;
    }
  }
  return venue;
};

export const getEventEditURL = (event: EventVO): string => {
  return window.PMW.util.site_url(`event/wizard/${event.hashedID}`);
};

export const getEventSignupFormEditUrl = (signupFormID: string): string => {
  return window.PMW.util.site_url(`formmaker/load/${signupFormID}`);
};

export const isCurrentUserEventCreator = (event: EventVO): boolean => {
  const currentUserId = getUserId();

  if (!currentUserId) {
    return false;
  }

  return isUserEventCreator(event, currentUserId);
};

export const isUserEventCreator = (event: EventVO, userId: number): boolean => {
  return event.creatorId === userId;
};

export const isEventShared = (event: EventVO): boolean => {
  return !isCurrentUserEventCreator(event);
};

export const isEventSharedExternally = (event: EventVO): boolean => {
  return !!event.eventAccessInfo?.isExternallyShared;
};

export const doesUserHaveReadOnlyAccessForEvent = (event: EventVO): boolean => {
  return isEventSharedExternally(event);
};

export const canUserPerformWriteActionToEvent = (event: EventVO): boolean => {
  return isCurrentUserEventCreator(event) || !isEventSharedExternally(event);
};

export const isUserEventOwnerOrTeamAdmin = (event: EventVO): boolean => {
  return isCurrentUserEventCreator(event) || isUserEventTeamAdmin(event);
};

export const isUserEventTeamAdmin = (event: EventVO): boolean => {
  return !!event.eventAccessInfo?.isUserEventTeamAdmin;
};

export const getEventsLazyDataBasedOnAppliedFilters = (state: EventsReduxState, _eventsViewType: EventsViewType, eventFilters: EventsFilters): EventsListLazyData => {
  if (areEventFiltersInDefaultState(eventFilters)) {
    return state.eventsListDefaultLazyData;
  }
  return state.eventsListFilteredLazyData;
};

export const getFilteredEvents = (events: EventVO[], filters: EventsFilters): EventVO[] => {
  return events.filter((userEvent) => {
    return (
      isEventIncludedInSearch(userEvent, filters.searchTerm) &&
      filters.timeFilter[getEventTimeBasedStatus(userEvent)] &&
      isEventIncludedInShareEventFilter(userEvent, filters.sharedEventsActiveFilter)
    );
  });
};

export const filterEventsBySearch = (events: EventVO[], searchTerm: string): EventVO[] => {
  return events.filter((userEvent) => {
    return isEventIncludedInSearch(userEvent, searchTerm);
  });
};

export const sortEvents = (events: EventVO[], sortEventsBy: EventSortBy): EventVO[] => {
  if (sortEventsBy === EventSortBy.EVENT_START_DATE) {
    return events.sort((event1, event2) => {
      return sortEventsByDates(event1.time?.date_start ?? '', event2.time?.date_start ?? '');
    });
  }

  return events.sort((event1, event2) => {
    return sortEventsByDates(event1.lastModified, event2.lastModified);
  });
};

export const filterEvents = (events: EventVO[], filters: EventsFilters): EventVO[] => {
  return sortEvents(getFilteredEvents(events, filters), filters.sortType);
};

export const openUserEventPreviewDialog = (event: EventVO, additionalProps: Omit<EventsIframeModalProps, 'event' | 'iframeTitle' | 'iframeURL'> = {}): void => {
  openEventPreviewModal({
    iframeTitle: event.title,
    iframeURL: getEventPageURL(event),
    event,
    ...additionalProps,
  });
};

export const openEventVenueExternally = (event: EventVO): void => {
  if (event.venue?.googleMapUrl) {
    window.open(event.venue.googleMapUrl, '_blank');
    return;
  }

  openOnlineEventLink(event);
};

export const openOnlineEventLink = (event: EventVO): void => {
  if (!event.venueOnline?.link || isPastEvent(event)) {
    return;
  }

  window.open(event.venueOnline.link, '_blank');
};

export const getUTCEventStartDate = (event: EventVO): Date => {
  return getDateFromUnixTimestamp(event.startDateTimestamp);
};

export const getUTCEventEndDate = (event: EventVO): Date => {
  return getDateFromUnixTimestamp(event.endDateTimestamp);
};

/**
 * event startDateTime is of format: 2025-03-24 07:00:00 this is in UTC time, we convert it to timezone time using timezone offset in format +02:00
 * @param event
 */
export const getTimezoneAdjustedEventStartDate = (event: EventVO): Date => {
  return convertUTCTimeToGivenTimezone(new Date(event.startDateTime), event.timezoneOffset ?? '+0:00');
};

export const getTimezoneAdjustedEventEndDate = (event: EventVO): Date => {
  return convertUTCTimeToGivenTimezone(new Date(event.endDateTime), event.timezoneOffset ?? '+0:00');
};

export const isOngoingEvent = (event: EventVO): boolean => {
  const eventStartDate = getUTCEventStartDate(event);
  const eventEndDate = getUTCEventEndDate(event);
  const currentDate = new Date();
  return isBetweenDates(currentDate, eventStartDate, eventEndDate);
};

export const isPastEvent = (event: EventVO): boolean => {
  return hasDatedPassed(getUTCEventEndDate(event));
};

export const isUpcomingEvent = (event: EventVO): boolean => {
  return isFutureDate(getUTCEventStartDate(event));
};

export const isRecurringEvent = (event: EventVO): boolean => {
  return !!event.recurring;
};

export const shouldShowRecurringEventRelatedInfo = (event: EventVO): boolean => {
  return isRecurringEvent(event) && !!event.doShowRecurringEventInfo;
};

export const isDailyRecurringEvent = (recurringEventPattern?: RecurringEvent): boolean => {
  return recurringEventPattern?.recurringType === RecurringType.DAILY;
};

export const isMonthlyRecurringEvent = (recurringEventPattern?: RecurringEvent): boolean => {
  return recurringEventPattern?.recurringType === RecurringType.MONTHLY && !!recurringEventPattern.recurringMonthly;
};

export const isSingleDayWeeklyRecurringEvent = (recurringEventPattern?: RecurringEvent): boolean => {
  return recurringEventPattern?.recurringType === RecurringType.WEEKLY && recurringEventPattern.recurringWeeks?.length === 1;
};

export const isMultipleDayWeeklyRecurringEvent = (recurringEventPattern?: RecurringEvent): boolean => {
  return recurringEventPattern?.recurringType === RecurringType.WEEKLY && recurringEventPattern.recurringWeeks?.length > 1;
};

export const getWeekdayForSingleDayWeeklyRecurringEvent = (recurringEventPattern: RecurringEvent): string => {
  return getTranslatedWeekdays()[ALL_WEEKDAYS.indexOf(recurringEventPattern.recurringWeeks[0])];
};

export const getPluralWeekdayForSingleDayWeeklyRecurringEvent = (recurringEventPattern: RecurringEvent): string => {
  return getTranslatedPluralWeekdaysForEvents()[ALL_WEEKDAYS.indexOf(recurringEventPattern.recurringWeeks[0])];
};

export const getMonthlyRecurringEventWeekdayInfoString = (recurringEventPattern: RecurringEvent): string => {
  return `${recurringEventPattern.recurringMonthly.weekdayNum} ${getExtraShortWeekdayString(recurringEventPattern.recurringMonthly.weekday)}`;
};

export const doesEventSpanMultipleDays = (event: EventVO): boolean => {
  const eventStartDate = getTimezoneAdjustedEventStartDate(event);
  const eventEndDate = getTimezoneAdjustedEventEndDate(event);
  return !areDatesEqual(eventStartDate, eventEndDate);
};

export const getMultiDayFormattedEventDateText = (eventStartDate: Date, eventEndDate: Date): string => {
  return `${eventStartDate.getDate()} ${getReadableShortMonth(eventStartDate)} - ${eventEndDate.getDate()} ${getReadableShortMonth(eventEndDate)}`;
};

export const loadFontFamilyInIframe = (iframeDocument: Document, fontFamily: string): void => {
  const elements = iframeDocument.querySelectorAll('.js-custom-font-family-text');

  elements.forEach((el) => {
    el.classList.add('-loading');
  });

  appendFontStyleSheetToDOM(fontFamily, getFontStyleSheetPath(fontFamily), iframeDocument, () => {
    iframeDocument.fonts.ready
      .then(() => {
        loadAndApplyFontFamilyInIframe(iframeDocument, fontFamily, elements);
      })
      .catch(noop);
  });
};

export const loadAndApplyFontFamilyInIframe = (iframeDocument: Document, fontFamily: string, elementsToApplyFontOn: NodeListOf<Element>): void => {
  void iframeDocument.fonts.load(`16px '${fontFamily}'`).then(() => {
    // Apply the font family and remove the '-loading' class for all matching elements
    elementsToApplyFontOn.forEach((el) => {
      (el as HTMLHeadingElement | HTMLSpanElement).style.setProperty('font-family', getFontFamilyStyleString(fontFamily), 'important');
      el.classList.remove('-loading');
    });
  });
};

export const getDefaultLazyData = (loadingState: LoadingStates = LoadingStates.NOT_LOADED): EventsListLazyData => {
  return {
    ids: [],
    loadingState,
    loadMore: true,
  };
};

export const areEventFiltersInDefaultState = (filters: EventsFilters): boolean => {
  return (
    filters.sortType === EventSortBy.EVENT_START_DATE &&
    areAllEventStatusesSelectedInFilter(filters.timeFilter) &&
    isAllSharedEventsFilterSelected(filters.sharedEventsActiveFilter)
  );
};

export const isMyEventsViewType = (eventsViewType: EventsViewType): boolean => {
  return eventsViewType === EventsViewType.DEFAULT;
};

export const isSharedWithMeEventsViewType = (eventsViewType: EventsViewType): boolean => {
  return eventsViewType === EventsViewType.SHARED_WITH_ME;
};

export const isAllSharedEventsFilterSelected = (eventSharedFilterValue: EventsSharedWithMeFilter): boolean => {
  return eventSharedFilterValue === EventsSharedWithMeFilter.ALL;
};

export const isCoPromotersSharedFilterSelected = (eventSharedFilterValue: EventsSharedWithMeFilter): boolean => {
  return eventSharedFilterValue === EventsSharedWithMeFilter.COPROMOTERS;
};

export const isTeamValueSelectedInEventsSharedFilter = (eventSharedFilter: EventsSharedWithMeFilter): boolean => {
  return eventSharedFilter === EventsSharedWithMeFilter.TEAM;
};

export const areAllEventStatusesSelectedInFilter = (statusFilter: EventTimeFilterMap): boolean => {
  return (
    Object.values(statusFilter).find((val) => {
      return !val;
    }) === undefined
  );
};

/**
 * Returns like 'Mo, Tu, We'
 * @param recurringPattern
 */
export const getExtraShortWeekdaysString = (recurringPattern?: RecurringEvent): string | null => {
  if (!recurringPattern || recurringPattern.recurringType !== RecurringType.WEEKLY) {
    return null;
  }

  const weekdaysArraySorted = [...recurringPattern.recurringWeeks];
  weekdaysArraySorted.sort((a, b) => {
    return ALL_WEEKDAYS.indexOf(a) - ALL_WEEKDAYS.indexOf(b);
  });

  const extraShortWeekdays = [];

  for (const weekday of weekdaysArraySorted) {
    extraShortWeekdays.push(getExtraShortWeekdayString(weekday));
  }

  return weekdaysArraySorted.map((weekday) => getExtraShortWeekdayString(weekday)).join(', ');
};

export const getExtraShortWeekdayString = (weekday: Weekdays): string => {
  if (weekday === Weekdays.MONDAY) {
    return window.i18next.t('pmwjs_calendar_mo');
  }

  if (weekday === Weekdays.TUESDAY) {
    return window.i18next.t('pmwjs_calendar_tu');
  }

  if (weekday === Weekdays.WEDNESDAY) {
    return window.i18next.t('pmwjs_calendar_we');
  }

  if (weekday === Weekdays.THURSDAY) {
    return window.i18next.t('pmwjs_calendar_th');
  }

  if (weekday === Weekdays.FRIDAY) {
    return window.i18next.t('pmwjs_calendar_fr');
  }

  if (weekday === Weekdays.SATURDAY) {
    return window.i18next.t('pmwjs_calendar_sa');
  }

  if (weekday === Weekdays.SUNDAY) {
    return window.i18next.t('pmwjs_calendar_su');
  }
  return '';
};

export const getRecurringEventDateText = (recurringEventPattern?: RecurringEvent): string => {
  if (!recurringEventPattern) {
    return '';
  }

  if (recurringEventPattern.recurringType === RecurringType.DAILY) {
    return window.i18next.t('pmwjs_daily');
  }

  if (recurringEventPattern.recurringType === RecurringType.MONTHLY && recurringEventPattern.recurringMonthly?.weekday) {
    const weekdayInd = ALL_WEEKDAYS.indexOf(recurringEventPattern?.recurringMonthly?.weekday);
    return window.i18next.t('pmwjs_monthly_x_weekday', {number: recurringEventPattern.recurringMonthly.weekdayNum, weekday: getTranslatedWeekdays()[weekdayInd]});
  }

  if (recurringEventPattern.recurringType === RecurringType.WEEKLY && recurringEventPattern.recurringWeeks?.length > 0) {
    if (recurringEventPattern.recurringWeeks.length > 1) {
      const extraShortWeekdaysString = getExtraShortWeekdaysString(recurringEventPattern);
      return window.i18next.t('pmwjs_every_weekdays', {weekdays: extraShortWeekdaysString});
    }
    const weekdayInd = ALL_WEEKDAYS.indexOf(recurringEventPattern.recurringWeeks[0]);
    return window.i18next.t('pmwjs_every_weekday', {weekday: getTranslatedWeekdays()[weekdayInd]});
  }
  return '';
};

/**
 * Base text: Daily, Weekly, Monthly, any text that describes the recurring event without the specific details of the time
 */
export const getRecurringEventDateBaseText = (recurringEventPattern?: RecurringEvent): string => {
  if (!recurringEventPattern) {
    return '';
  }

  if (isDailyRecurringEvent(recurringEventPattern)) {
    return window.i18next.t('pmwjs_daily');
  }

  if (isMonthlyRecurringEvent(recurringEventPattern)) {
    return window.i18next.t('pmwjs_monthly');
  }

  if (isSingleDayWeeklyRecurringEvent(recurringEventPattern)) {
    return getPluralWeekdayForSingleDayWeeklyRecurringEvent(recurringEventPattern);
  }

  if (isMultipleDayWeeklyRecurringEvent(recurringEventPattern)) {
    return window.i18next.t('pmwjs_weekly');
  }

  return '';
};

/**
 * constructs the descriptive part of a recuring events' date.
 * for example: Tu, We, Th , or: 1st Mo
 * @param recurringEventPattern
 */
export const getRecurringEventDateDescriptiveText = (recurringEventPattern?: RecurringEvent): string => {
  if (!recurringEventPattern) {
    return '';
  }

  if (isMonthlyRecurringEvent(recurringEventPattern)) {
    return getMonthlyRecurringEventWeekdayInfoString(recurringEventPattern);
  }

  if (isMultipleDayWeeklyRecurringEvent(recurringEventPattern)) {
    return getExtraShortWeekdaysString(recurringEventPattern) ?? '';
  }

  return '';
};

/**
 * constructs the full text for example: Weekly · Tu, We, Th
 */
export const getRecurringEventTextWithDetails = (recurringEventPattern?: RecurringEvent): string => {
  const baseText = getRecurringEventDateBaseText(recurringEventPattern);
  const descriptiveText = getRecurringEventDateDescriptiveText(recurringEventPattern);

  if (!descriptiveText) {
    return baseText;
  }

  return `${baseText} · ${descriptiveText}`;
};

export const doDeleteEvent = async (eventHashedId: EventHashedID, onDeleteSuccess: VoidFunction, deleteAllRecurringEvents = false): Promise<void> => {
  showLoading('deletingEvent', {
    text: window.i18next.t('pmwjs_deleting'),
  });

  try {
    await window.PMW.writeLocal('event/deleteEvent', {
      event_hid: eventHashedId,
      deleteAllRecurringEvents,
    });
  } catch (err) {
    onAjaxError(err);
    return;
  }

  onDeleteSuccess();
  hideLoading('deletingEvent');
};

export const startEventRemovalFlow = (panelProps: DeleteEventConfirmationPanelProps): void => {
  openDeleteEventConfirmationModal(panelProps);
};

export const getDummyEventItem = (): EventVO => {
  return {
    hashedID: getUniqueString(),
    title: '',
    description: '',
    plainTextDescription: '',
    endDateTime: '',
    startDateTime: '',
    startDateTimestamp: getUnixTimestamp(new Date()),
    endDateTimestamp: getUnixTimestamp(new Date()),
    numRegistrations: 0,
    showCountdown: false,
    isRemoveWatermark: false,
    isShared: false,
    isPast: false,
    tzcode: '',
    registrationFields: {
      areRegistrationsRequired: true,
      email: false,
      name: false,
      phone: false,
      registerBtnText: '',
    },
    organiser: {
      name: '',
      email: '',
      profilePic: '',
      isProfilePicturePrefilled: false,
      socialMediaLinks: initSocialMediaProfileLinks(),
    },
    status: EventStatus.DRAFT,
    type: {
      hashedID: '',
      name: '',
    },
    lastModified: '',
    style: {
      fontFamily: '',
      colors: {
        primary: '',
        light: '',
        lighter: '',
        dark: '',
        darker: '',
      },
    },
    allowRegistrations: false,
    creatorId: -1,
    timezoneOffsetWithAbbreviation: 'GMT+5',
    timezoneOffset: '+5',
    numViews: 0,
  };
};

export const getDummyCalendarMetaObject = (): EventCalendarMetaData => {
  return {
    calendarName: '',
    calendarHID: '',
    topXEventImages: [],
    numLiveFutureEvents: 0,
    numUpcomingEventsThisWeek: 0,
    numSocialConnectedAccounts: 0,
    singleSocialPlatformName: null,
    numMailingLists: 0,
    hasSenderProfile: false,
  };
};

export const getSortedEventsBasedOnLiveStatus = (eventsList: EventVO[]): EventVO[] => {
  return eventsList.sort((event1, event2) => {
    const isEvent1Ongoing = isOngoingEvent(event1);
    const isEvent2Ongoing = isOngoingEvent(event2);
    if (isEvent1Ongoing && !isEvent2Ongoing) {
      return -1;
    }

    if (isEvent2Ongoing && !isEvent1Ongoing) {
      return 1;
    }

    return 0;
  });
};

export const openEventsUpsellDialog = (upsellType: EventUpsellType, onDialogLoaded = noop): void => {
  switch (upsellType) {
    case EventUpsellType.MORE_EVENTS:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.MORE_EVENTS,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_MORE_EVENTS, onDialogLoaded);
      return;

    case EventUpsellType.MORE_EVENTS_PREMIUM:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.MORE_EVENTS_PREMIUM,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_PLUS_ONLY_FEATURE_NAME_MORE_EVENTS, onDialogLoaded);
      return;

    case EventUpsellType.REMOVE_WATERMARK:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.REMOVE_WATERMARK,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_PLUS_ONLY_FEATURE_EVENTS_REMOVE_WATERMARK, onDialogLoaded);
      return;

    case EventUpsellType.DESCRIPTION_REWRITE_AI:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.DESCRIPTION_REWRITE_AI,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENTS_AI_WRITER, onDialogLoaded);
      return;

    case EventUpsellType.REGISTRATION_OPTIONS:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.REGISTRATION_OPTIONS,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENTS_GUESTS_INFORMATION, onDialogLoaded);
      return;

    case EventUpsellType.RECURRING:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.RECURRING,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_PLUS_ONLY_FEATURE_NAME_RECURRING_EVENTS, onDialogLoaded);
      return;

    case EventUpsellType.NUM_GUESTS_LIMIT:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.NUM_GUESTS_LIMIT,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENTS_LIMITED_REGISTRATONS, onDialogLoaded);
      return;

    case EventUpsellType.VIEW_ANALYTICS:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.VIEW_ANALYTICS,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENTS_VIEW_ANALYTICS, onDialogLoaded);
      return;

    case EventUpsellType.REMINDERS:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.REMINDERS,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENTS_REMINDERS, onDialogLoaded);
      return;

    case EventUpsellType.SHARE_TEAM:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.SHARE_TEAM,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_TEAMS, onDialogLoaded);
      return;

    case EventUpsellType.SIGNUP_FORM:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.SIGNUP_FORM,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EMAIL_CAMPAIGNS_AUDIENCE, onDialogLoaded);
      return;

    case EventUpsellType.EVENT_ONE_CLICK_PROMOTION:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.EVENT_ONE_CLICK_PROMOTION,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENT_ONE_CLICK_PROMOTION, onDialogLoaded);
      return;
  }
};

export const openUpsellDialogForEventsLimitReached = (onDialogLoaded = noop): void => {
  openEventsUpsellDialog(isPAYGUser() ? EventUpsellType.MORE_EVENTS : EventUpsellType.MORE_EVENTS_PREMIUM, onDialogLoaded);
};

export const startCreateSocialPostFromEventFlow = (eventHashedID: EventHashedID): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_PROMOTE_SOCIAL);
  window.location.href = window.PMW.util.site_url(`socialmedia/post/event/${eventHashedID}`);
};

export const startCreateEmailCampaignFromEventFlow = (eventHashedID: EventHashedID): void => {
  if (isPAYGUser()) {
    openUpsellingDialogForEmailCampaigns();
    return;
  }

  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_PROMOTE_EMAIL);
  window.PMW.redirectUrlCache.setCache(
    window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_CACHE_KEY,
    window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_REDIRECT_URI + `/${eventHashedID}`,
    window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_DEFAULT_CACHE_TIMEOUT_MILLISECONDS
  );

  window.open(window.PMW.util.site_url(`emailmarketing/publish/event/${eventHashedID}`), '_self');
};

export const getTimeZoneFromTzCode = (tzcode: string): TimeZone => {
  return findTimeZoneFromTzCode(tzcode) ?? getUserDefaultTimeZone();
};

export const getUserDefaultTimeZone = (): TimeZone => {
  const userTimeZone = findTimeZoneFromTzCode(Intl.DateTimeFormat().resolvedOptions().timeZone);

  if (userTimeZone) {
    return userTimeZone;
  }
  const userUTCOffsetMins = -1 * new Date().getTimezoneOffset();

  const findApproximateUserTimeZoneFromUTC = (utcOffsetMins: number): TimeZone | undefined => {
    return timezones.find((tz: TimeZone) => {
      return getGMTOffsetMinutesFromTimeZone(tz) === utcOffsetMins;
    });
  };

  return findApproximateUserTimeZoneFromUTC(userUTCOffsetMins) ?? timezones[0];
};

export const findTimeZoneFromTzCode = (userTzCode: string): TimeZone | undefined => {
  return timezones.find((tz: TimeZone) => {
    return tz.tzCode === userTzCode;
  });
};

export const getEventPagePreviewURL = (eventHashedId: EventHashedID, previewType: EventPagePreviewType): string => {
  return repoURL(`eventpagepreviews/${eventHashedId}-${previewType}.jpg?ts=${getUnixTimestamp(new Date())}`, getWriteBucket());
};

export const getGalleryUriFromEventAiSearchParams = (aiSearchParams: EventTypeAiSearchParams | undefined): string => {
  if (aiSearchParams === undefined) {
    return GalleryThemeURI.EVENTS;
  }
  const {eventName, eventTypeHashedId} = aiSearchParams,
    eventPosterTemplatesParams = new URLSearchParams();
  eventPosterTemplatesParams.set('event_name', eventName);
  eventPosterTemplatesParams.set('event_type_hashed_id', eventTypeHashedId);
  return `posters/searchEventPosterTemplates?${eventPosterTemplatesParams.toString()}`;
};

export const hasUserReachedPAYGLimitForEventRegistrations = (event: EventVO): boolean => {
  return isPAYGUser() && event.numRegistrations >= EVENT_REGISTRATIONS_LIMIT_PAYG;
};

export const createNewPendingInvitesFromEmails = (emails: string[]): EventPendingEmailInvitation[] => {
  return emails.map((email): EventPendingEmailInvitation => {
    return {email, isExpired: false, profilePictureURL: window.PMW.util.asset_url('images/default_profile_picture_small.png')};
  });
};

export const getGA4EventForSkippedPromotionType = (promotionType: PromotionType): GA4EventName | string => {
  switch (promotionType) {
    case PromotionType.EMAIL_CAMPAIGNS:
      return GA4EventName.EVENTS_PROMOTE_EMAIL_SKIP;

    case PromotionType.EMBED_IN_WEBPAGE:
      return GA4EventName.EVENTS_PROMOTE_EMBED_SKIP;

    case PromotionType.PRINT_FLYERS:
      return GA4EventName.EVENTS_PROMOTE_PRINT_SKIP;

    case PromotionType.SHARE_EVENT_PAGE_LINK:
      return GA4EventName.EVENTS_PROMOTE_PAGE_SHARE_SKIP;

    case PromotionType.SOCIAL_POSTS:
      return GA4EventName.EVENTS_PROMOTE_SOCIAL_SKIP;

    default:
      return '';
  }
};

const sortEventsByDates = (date1: string, date2: string): number => {
  return new Date(date2).getTime() - new Date(date1).getTime();
};

const isEventIncludedInSearch = (userEvent: EventVO, searchTerm: string): boolean => {
  return userEvent.title.toLowerCase().includes(searchTerm.toLowerCase());
};

export const isEventIncludedInShareEventFilter = (event: EventVO, eventSharedFilter: EventsSharedWithMeFilter): boolean => {
  if (isAllSharedEventsFilterSelected(eventSharedFilter)) {
    return true;
  }

  if (isCoPromotersSharedFilterSelected(eventSharedFilter)) {
    return isEventSharedExternally(event);
  }

  if (isTeamValueSelectedInEventsSharedFilter(eventSharedFilter)) {
    return event.isShared;
  }

  return true;
};

/**
 *
 */
const getEventTimeBasedStatus = (userEvent: EventVO): EventTimeFilter => {
  if (isOngoingEvent(userEvent)) {
    return EventTimeFilter.LIVE;
  }

  if (isUpcomingEvent(userEvent)) {
    return EventTimeFilter.UPCOMING;
  }

  if (isPastEvent(userEvent)) {
    return EventTimeFilter.PAST;
  }

  return EventTimeFilter.PAST;
};

const areQueryParamsNotNeededForEventPageSource = (prefillSource: EventPageSource): boolean => {
  return prefillSource === EventPageSource.DEFAULT;
};

export const getUserEvent = (eventHashedId: string): Promise<EventVO> => {
  return window.PMW.readLocal('event/getEvent', {
    event_hid: eventHashedId,
  });
};

export const getUserEvents = (eventsFilters: EventsFilters): Promise<EventVO[]> => {
  return window.PMW.readLocal('event/getUserEventsForMyStuff', {
    eventsViewType: EventsViewType.DEFAULT,
    timeFilters: eventsFilters.timeFilter,
    sort: eventsFilters.sortType,
    searchTerm: eventsFilters.searchTerm,
    offset: 0,
  }) as Promise<EventVO[]>;
};

export const initEventPromoteOptionBorderAnimationSeenState = (): void => {
  if (typeof window.PMW.hasSeenPromoteOptionAnimationInCurrentSession === 'undefined') {
    window.PMW.hasSeenPromoteOptionAnimationInCurrentSession = false;
  }
};

export const markEventPromoteOptionBorderAnimationAsSeen = (): void => {
  initEventPromoteOptionBorderAnimationSeenState();
  window.PMW.hasSeenPromoteOptionAnimationInCurrentSession = true;
};

export const shouldShowEventPromoteOptionBorderAnimation = (): boolean => {
  initEventPromoteOptionBorderAnimationSeenState();
  return !window.PMW.hasSeenPromoteOptionAnimationInCurrentSession;
};

export const getSecondaryColorForEventsCalendar = (primaryColor: string): string => {
  const textColorWithPrimaryColorAsBg = getColorPaletteForContainer(primaryColor).textColor;

  if (textColorWithPrimaryColorAsBg === PMW_COLORS_NEUTRAL.NEUTRAL_0) {
    return primaryColor;
  }

  return textColorWithPrimaryColorAsBg;
};
